import { useState, useEffect, useMemo } from "react"
import keyBy from "lodash.keyby"

import { useAppContext } from "@components/AppContext"
import { PROJECT_TYPES } from "@components/Domain/Constants"
// NOTE: Bug in Safari, WTF
// eslint-disable-next-line no-unused-vars
import { extendProjectTags } from "@modules/backstage/projects/ProjectUpdateModal/helpers/ProjectTags"

const readProjectOperation = "backstage/ReadProject"
const indexProjectsOperation = "backstage/ListProjects"
const updatePropertyOperation = "backstage/UpdateProperty"
const disconnectFundProjectPropertyOperation = "backstage/DisconnectFundProjectProperty"

const LABEL_CLASS = "Class"


const useProjectsStore = () => {
  const { request, identity } = useAppContext()
  const { id: organizationId } = identity.organization

  const [ projects, setProjects ] = useState()

  let isInProgress = false
  useEffect(() => {
    if (isInProgress) { return }
    isInProgress = true

    indexProjects()
  }, [ organizationId ])

  const indexProjects = () =>
    request(indexProjectsOperation, {})
      .then(({ data }) => setProjects(data))

  const getProject = projectId =>
    projects.find(({ id }) => id === projectId)

  const addProject = newProject =>
    setProjects([ newProject, ...projects])

  const updateProject = (updatedProjectAttributes, shouldOverride = false) => {
    const projectIndex = projects
      .findIndex(({ id }) => id === updatedProjectAttributes.id)

    if (shouldOverride) {
      projects[projectIndex] = updatedProjectAttributes

    } else {
      projects[projectIndex] = {
        ...projects[projectIndex],
        ...updatedProjectAttributes
      }

    }

    setProjects([ ...projects ])
  }

  const readProject = id =>
    request(readProjectOperation, { id })
      .then(({ data }) => {
        updateProject(data, true)
        return data
      })

  const deleteProject = projectId => {
    const projectIndex = projects.findIndex(({ id }) => id === projectId)
    projects.splice(projectIndex, 1)

    setProjects([...projects])
  }

  const addProjectProperty = (createdProperty) => {
    const { projectId } = createdProperty

    const { investmentSubjects: properties } = getProject(projectId)
    const investmentSubjects = [createdProperty, ...properties]

    updateProject({ id: projectId, investmentSubjects })
  }

  const updateProjectProperty = (projectId, updatedProperty) => {
    const { id: propertyId } = updatedProperty

    if (!propertyId) {
      return
    }

    const { investmentSubjects: properties } = getProject(projectId)

    const propertyIndex = properties.findIndex(({ id }) => id === propertyId)
    properties[propertyIndex] = { ...properties[propertyIndex], ...updatedProperty }

    updateProject({ id: projectId, investmentSubjects: properties })
  }

  const removeProjectProperty = async (projectId, propertyId) => {
    const project = getProject(projectId)

    if (!project) { return }

    const isFund = project.type === PROJECT_TYPES.FUND

    if (isFund) {
      const mutation = { propertyId }
      await request(disconnectFundProjectPropertyOperation, { id: project.id, mutation })

    } else {
      const mutation = { projectId: "" }
      await request(updatePropertyOperation, { id: propertyId, mutation })

    }

    const { investmentSubjects: properties = [] } = project

    const hasProperties = properties.length > 0

    if (!hasProperties) { return }

    const itemIndex = properties.findIndex(property => property.id === propertyId)
    properties.splice(itemIndex, 1)

    updateProject({ id: projectId, investmentSubjects: properties })
  }

  const readConnectedFundProjects = async (propertyId) => {
    const connectedFundProjectIds = projects
      .filter(({ type }) => type === PROJECT_TYPES.FUND)
      .filter(({ investmentSubjectIds = [] }) => investmentSubjectIds.includes(propertyId))
      .map(({ id }) => id)

    for (const fundProjectId of connectedFundProjectIds) {
      readProject(fundProjectId)
    }
  }

  const projectsMap = useMemo(() => keyBy(projects, "id"), [ projects ])

  const getProjectName = id => getProject(id)?.name || id

  const getTierName = ({ name, title }) => title || `${LABEL_CLASS} ${name}`

  const store = {
    projects,
    getProject,
    addProject,
    readProject,
    getTierName,
    projectsMap,
    updateProject,
    deleteProject,
    getProjectName,
    addProjectProperty,
    updateProjectProperty,
    removeProjectProperty,
    readConnectedFundProjects,
  }

  return store
}

export default useProjectsStore
